import React, { useState } from 'react';
import Localize from 'react-intl-universal';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { formatLongDateTimeStringToDateTimeObject, formatPrice } from '@common/Util';
import AudioPreview from '@components/AudioPreview/AudioPreview';
import LanguageAndFlag from '@components/LanguageAndFlag/LanguageAndFlag';
import RichTextEditor from '@components/RichTextEditor/RichTextEditor';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  minHeight: '30rem',
  maxHeight: '50rem',
  margin: 'auto',
  overflowY: 'scroll',
  overflowX: 'hidden',
  '& .MuiDialog-paper': {
    backgroundColor: `${theme.palette.booking.background}!important`,
    color: `${theme.palette.primary.contrastText}!important`
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  textWrap: 'wrap',
  backgroundColor: theme.palette.grey[300],
  padding: '6px',
  borderRadius: '8px',
  margin: '0.5rem'
}));

const StyleTypography = styled(Typography)(({ theme }) => ({
  color: [theme.palette.primary.contrastText]
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none !important',
  fontSize: '14px !important',
  color: `${theme.palette.booking.button}!important`
}));

const renderMultipleValues = (arr, title) => {
  if (!arr || arr?.length === 0) return null;

  return (
    <StyledBox>
      <Typography display='inline'>{Localize.get(title) + ': '}</Typography>
      <Tooltip
        title={
          arr?.length > 1 ? (
            <ul style={{ padding: 0, fontSize: '1rem' }}>
              {arr.map((location, index) => {
                if (index) return <li key={index}>{location}</li>;
              })}
            </ul>
          ) : (
            ''
          )
        }
      >
        <StyleTypography sx={{ paddingRight: '1.875rem' }} display='inline'>
          {arr[0]}
          {arr.length > 1 && <Box ml={2} component={'span'}>{`+${arr.length - 1}`}</Box>}
        </StyleTypography>
      </Tooltip>
    </StyledBox>
  );
};

const SessionDialog = ({
  session: {
    name,
    startDateTime,
    endDateTime,
    price,
    standardPrice,
    description,
    nativeLanguages,
    meetingRoomsDtos,
    optionalSession,
    audioDescription
  },
  trainers
}) => {
  const [open, setOpen] = useState(false);

  const locationNames = [];
  const meetingRoomNames = [];

  meetingRoomsDtos?.forEach((room) => {
    const temp = `${room?.venue?.city} ${room?.venue?.city && room?.venue?.country && ','}   ${room?.venue?.country}`;
    if (!locationNames.includes(temp)) {
      locationNames.push(temp);
    }
    if (!meetingRoomNames.includes(room?.name)) {
      meetingRoomNames.push(room?.name);
    }
  });

  const sessionPrice = formatPrice(price);
  const sessionCurrency = standardPrice?.currency?.value || '';

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      <StyledButton onClick={handleClickOpen}>{Localize.get('Booking.fullDetails')}</StyledButton>
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth={true}
        maxWidth='md'
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: '10px', sm: '32px' },
            width: { xs: '100%', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        <DialogTitle
          id='alert-dialog-title'
          sx={(theme) => ({
            paddingBottom: '0.3rem',
            color: theme.palette.primary.contrastText,
            fontWeight: '700'
          })}
        >
          {name}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[650]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            paddingTop: 0,
            pl: { xs: '0.5rem', sm: '1.5rem' },
            pr: { xs: '0.5rem', sm: '1.5rem' }
          }}
        >
          <Box>
            <Typography display='inline'>{Localize.get('Booking.trainer') + ': '}</Typography>
            <StyleTypography display='inline'>{trainers || ''}</StyleTypography>
          </Box>
          <Box sx={{ m: '0.8rem 0' }}>
            <Typography display='inline'>{`${formatLongDateTimeStringToDateTimeObject(startDateTime)} - `}</Typography>
            <Typography display='inline'>{`${formatLongDateTimeStringToDateTimeObject(endDateTime)}`}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexWrap: 'wrap'
            }}
          >
            {renderMultipleValues(locationNames, 'Booking.location')}
            {renderMultipleValues(meetingRoomNames, 'Booking.meetingRoom')}

            {nativeLanguages?.length ? (
              <StyledBox>
                <Typography display='inline'>
                  {Localize.get(
                    nativeLanguages?.length > 1 ? 'CourseDetails.langs' : 'Booking.language'
                  ) + ': '}
                </Typography>
                {nativeLanguages.map((language, index) =>
                  language?.key ? (
                    <LanguageAndFlag
                      key={index}
                      nativeLanguages={[language]}
                      showLabel={false}
                      containerSX={
                        index !== nativeLanguages.length - 1 ? { paddingRight: '0.5rem' } : {}
                      }
                    />
                  ) : null
                )}
              </StyledBox>
            ) : null}

            {optionalSession && (
              <StyledBox>
                <Typography display='inline'>{`${Localize.get('Booking.price')}: ${
                  parseInt(sessionPrice)
                    ? `${sessionPrice} ${sessionCurrency} `
                    : Localize.get('CustomerLoyaltyEvents.free')
                }`}</Typography>
              </StyledBox>
            )}
          </Box>
          <Paper
            sx={(theme) => ({
              flex: 1,
              margin: '1rem 0 0 0',
              padding: '0.5rem 1rem',
              backgroundColor: theme.palette.booking.dialogFooter
            })}
            variant='outlined'
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                mb: 1
              }}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.palette.primary.contrastText,
                  padding: '0',
                  fontWeight: '600'
                })}
                flex={1}
                variant='h6'
              >
                {Localize.get('Booking.sessionDescription')}
              </Typography>
              {audioDescription && (
                <AudioPreview url={audioDescription?.url} name={audioDescription?.name} />
              )}
            </Box>
            <StyleTypography component='div'>
              <RichTextEditor
                initialValue={description || Localize.get('EventDetails.noTopics')}
                disabled
                isToolbarHidden
                customStyles={{ minHeight: '50px', maxHeight: '170px' }}
              />
            </StyleTypography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: (theme) => theme.palette.booking.dialogFooter }}>
          <StyledButton onClick={handleClose}>{Localize.get('App.close')}</StyledButton>
        </DialogActions>
      </StyledDialog>
    </React.Fragment>
  );
};

export default SessionDialog;
